/**
 * State + thunks shared across the Org Settings tabs (Profile, Billing, etc).
 *
 * Owns the "save/delete owned org profile" flow that mutates the current
 * user's publicData.organizationProfiles[]. Team membership / role data
 * lives in OrgSettingsTeamsPage.duck — this duck does NOT touch
 * metadata.organizationMemberships or selectedOrganizationId (both legacy).
 */

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOrganizationProfiles, fetchOrganizationById } from '../../ducks/organizations.duck';

// ================ Action types ================ //

export const SAVE_ORGANIZATION_PROFILES_REQUEST =
  'app/OrgSettingsPage/SAVE_ORGANIZATION_PROFILES_REQUEST';
export const SAVE_ORGANIZATION_PROFILES_SUCCESS =
  'app/OrgSettingsPage/SAVE_ORGANIZATION_PROFILES_SUCCESS';
export const SAVE_ORGANIZATION_PROFILES_ERROR =
  'app/OrgSettingsPage/SAVE_ORGANIZATION_PROFILES_ERROR';

export const DELETE_ORGANIZATION_PROFILE_REQUEST =
  'app/OrgSettingsPage/DELETE_ORGANIZATION_PROFILE_REQUEST';
export const DELETE_ORGANIZATION_PROFILE_SUCCESS =
  'app/OrgSettingsPage/DELETE_ORGANIZATION_PROFILE_SUCCESS';
export const DELETE_ORGANIZATION_PROFILE_ERROR =
  'app/OrgSettingsPage/DELETE_ORGANIZATION_PROFILE_ERROR';

export const FETCH_LISTINGS_REQUEST = 'app/OrgSettingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/OrgSettingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/OrgSettingsPage/FETCH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  saveOrganizationProfilesInProgress: false,
  saveOrganizationProfilesError: null,
  deleteOrganizationProfileInProgress: false,
  deleteOrganizationProfileError: null,
  fetchListingsInProgress: false,
  fetchListingsError: null,
  listingIds: [],
};

const resultIds = listings => listings.map(l => l.id);

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SAVE_ORGANIZATION_PROFILES_REQUEST:
      return { ...state, saveOrganizationProfilesInProgress: true, saveOrganizationProfilesError: null };
    case SAVE_ORGANIZATION_PROFILES_SUCCESS:
      return { ...state, saveOrganizationProfilesInProgress: false, saveOrganizationProfilesError: null };
    case SAVE_ORGANIZATION_PROFILES_ERROR:
      return { ...state, saveOrganizationProfilesInProgress: false, saveOrganizationProfilesError: payload };

    case DELETE_ORGANIZATION_PROFILE_REQUEST:
      return { ...state, deleteOrganizationProfileInProgress: true, deleteOrganizationProfileError: null };
    case DELETE_ORGANIZATION_PROFILE_SUCCESS:
      return { ...state, deleteOrganizationProfileInProgress: false, deleteOrganizationProfileError: null };
    case DELETE_ORGANIZATION_PROFILE_ERROR:
      return { ...state, deleteOrganizationProfileInProgress: false, deleteOrganizationProfileError: payload };

    case FETCH_LISTINGS_REQUEST:
      return { ...state, fetchListingsInProgress: true, fetchListingsError: null };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchListingsInProgress: false,
        fetchListingsError: null,
        listingIds: resultIds(payload.data),
      };
    case FETCH_LISTINGS_ERROR:
      return { ...state, fetchListingsInProgress: false, fetchListingsError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const saveOrganizationProfilesRequest = () => ({ type: SAVE_ORGANIZATION_PROFILES_REQUEST });
export const saveOrganizationProfilesSuccess = () => ({ type: SAVE_ORGANIZATION_PROFILES_SUCCESS });
export const saveOrganizationProfilesError = e => ({
  type: SAVE_ORGANIZATION_PROFILES_ERROR,
  error: true,
  payload: e,
});

export const deleteOrganizationProfileRequest = () => ({ type: DELETE_ORGANIZATION_PROFILE_REQUEST });
export const deleteOrganizationProfileSuccess = () => ({ type: DELETE_ORGANIZATION_PROFILE_SUCCESS });
export const deleteOrganizationProfileError = e => ({
  type: DELETE_ORGANIZATION_PROFILE_ERROR,
  error: true,
  payload: e,
});

export const fetchListingsRequest = () => ({ type: FETCH_LISTINGS_REQUEST });
export const fetchListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data.data },
});
export const fetchListingsError = e => ({ type: FETCH_LISTINGS_ERROR, payload: e });

// ================ Thunks ================ //

/**
 * Save the current user's owned organization profiles to publicData.
 *
 * Owner-as-member is now inferred from publicData.organizationProfiles[]
 * alone (see server/api-util/orgPermissions.getUserRoleInOrg). No metadata
 * writes here.
 */
export const saveOrganizationProfiles = organizationProfiles => async (dispatch, getState, sdk) => {
  dispatch(saveOrganizationProfilesRequest());

  if (!Array.isArray(organizationProfiles)) {
    dispatch(saveOrganizationProfilesError(new Error('Invalid organization profiles data')));
    return;
  }

  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  try {
    const response = await sdk.currentUser.updateProfile(
      { publicData: { organizationProfiles } },
      queryParams
    );

    dispatch(addMarketplaceEntities(response));
    dispatch(saveOrganizationProfilesSuccess());
    dispatch(fetchCurrentUser());
    dispatch(fetchOrganizationProfiles());
    return response;
  } catch (error) {
    console.error('Error saving organization profiles:', error);
    dispatch(saveOrganizationProfilesError(storableError(error)));
  }
};

/**
 * Delete one of the current user's owned organizations from
 * publicData.organizationProfiles. Returns the next org id to navigate to,
 * if any.
 */
export const deleteOrganizationProfile = (deletedProfile, organizationProfiles) => async dispatch => {
  dispatch(deleteOrganizationProfileRequest());

  try {
    const all = Array.isArray(organizationProfiles) ? organizationProfiles : [];
    const remaining = all.filter(o => o.id !== deletedProfile.id);

    const deletedIndex = all.findIndex(o => o.id === deletedProfile.id);
    let nextOrgId = null;
    if (remaining.length > 0) {
      const nextIndex = deletedIndex >= remaining.length ? remaining.length - 1 : deletedIndex;
      nextOrgId = remaining[nextIndex].id;
    }

    await dispatch(saveOrganizationProfiles(remaining));
    dispatch(deleteOrganizationProfileSuccess());
    return { success: true, nextOrgId };
  } catch (error) {
    console.error('Error deleting organization profile:', error);
    dispatch(deleteOrganizationProfileError(storableError(error)));
    throw error;
  }
};

export const fetchListings = organizationProfileId => (dispatch, getState, sdk) => {
  dispatch(fetchListingsRequest());

  return sdk.listings
    .query({ organizationId: organizationProfileId })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchListingsSuccess(response));
      return response;
    })
    .catch(e => dispatch(fetchListingsError(storableError(e))));
};

export const loadData = params => async dispatch => {
  const selectedOrganization = await dispatch(fetchOrganizationById(params.orgId));
  const selectedOrgId = selectedOrganization ? selectedOrganization.id : '';
  return Promise.all([
    dispatch(fetchOrganizationProfiles()),
    selectedOrgId ? dispatch(fetchListings(selectedOrgId)) : Promise.resolve(),
  ]);
};
